/**
 * Client-side Display Text Normalization
 * 
 * Safety net that ensures no raw LLM/user text leaks into the UI.
 * Mirrors the backend's normalizeToSystemLanguage() but runs on the client
 * as a last line of defense.
 */

// ── Narrative prefixes to strip ──
const NARRATIVE_PREFIXES = [
  /^(ich |wir |man |es |das |die |der |mein |unser |unsere |meine )/i,
  /^(wenn |falls |angesichts |aufgrund |wegen |anstatt |statt )/i,
  /^(es geht darum,?\s*)/i,
  /^(es handelt sich um\s*)/i,
  /^(die frage ist,?\s*(ob\s*)?)/i,
  /^(das bedeutet,?\s*)/i,
  /^(das würde bedeuten,?\s*)/i,
  /^(zum beispiel\s*)/i,
  /^(grundsätzlich\s*)/i,
  /^(im grunde\s*)/i,
  /^(sozusagen\s*)/i,
  /^(quasi\s*)/i,
  /^(eigentlich\s*)/i,
  /^(tatsächlich\s*)/i,
  /^(natürlich\s*)/i,
  /^(option [ab][\s:–-]+)/i,
];

// ── Filler words/phrases to remove mid-sentence ──
const FILLER_PATTERNS = [
  /\b(sozusagen|quasi|eigentlich|grundsätzlich|tatsächlich|natürlich|gewissermaßen|im Prinzip|im Grunde)\b/gi,
];

// ── Trailing fragment cleanup ──
const TRAILING_NOISE = [
  /[,;]\s*$/,            // trailing comma/semicolon
  /\s+(und|oder|bzw|also|dann|damit|um)\s*$/i,  // dangling conjunctions
  /\s*\.\.\.\s*$/,       // trailing ellipsis
  /\s*[–-]\s*$/,         // trailing dash
  /[(\[]\s*$/,           // unclosed bracket
  /\s+$/,                // trailing whitespace
];

// ── Per-field max lengths ──
const FIELD_MAX_LENGTHS: Record<string, number> = {
  optionA: 100,
  optionB: 100,
  sessionTitle: 60,
  coreConflict: 120,
  successDefinition: 120,
  stakesInaction: 120,
  trigger: 100,
  previousAttempts: 150,
  decisionType: 40,
  decisionSubtype: 50,
};

const DEFAULT_MAX_LENGTH = 150;

/**
 * Normalize a single display text value.
 * Strips narrative language, fillers, and enforces length limits.
 */
export function normalizeDisplayText(
  value: unknown,
  fieldName?: string,
): string {
  if (value === null || value === undefined) return '';
  
  let text = String(value).trim();
  if (!text) return '';
  
  // Don't normalize numeric-only values
  if (/^\d+(\.\d+)?$/.test(text)) return text;
  
  // Strip narrative prefixes (up to 2 passes for nested patterns)
  for (let pass = 0; pass < 2; pass++) {
    for (const pattern of NARRATIVE_PREFIXES) {
      text = text.replace(pattern, '');
    }
    text = text.trim();
  }
  
  // Capitalize first letter after stripping
  if (text.length > 0) {
    text = text.charAt(0).toUpperCase() + text.slice(1);
  }
  
  // Remove filler words
  for (const pattern of FILLER_PATTERNS) {
    text = text.replace(pattern, '');
  }
  
  // Collapse multiple spaces
  text = text.replace(/\s{2,}/g, ' ').trim();
  
  // Enforce max length
  const maxLen = (fieldName && FIELD_MAX_LENGTHS[fieldName]) || DEFAULT_MAX_LENGTH;
  if (text.length > maxLen) {
    // Cut at last word boundary before maxLen
    let truncated = text.slice(0, maxLen);
    const lastSpace = truncated.lastIndexOf(' ');
    if (lastSpace > maxLen * 0.6) {
      truncated = truncated.slice(0, lastSpace);
    }
    text = truncated;
  }
  
  // Clean trailing fragments
  for (const pattern of TRAILING_NOISE) {
    text = text.replace(pattern, '');
  }
  
  return text.trim();
}

/**
 * Normalize all string fields in an extraction fields record.
 * Non-string values (numbers, booleans) pass through unchanged.
 */
export function normalizeExtractionFields(
  fields: Record<string, { value: unknown; [key: string]: unknown }>,
): Record<string, { value: unknown; [key: string]: unknown }> {
  const result: Record<string, { value: unknown; [key: string]: unknown }> = {};
  
  for (const [key, field] of Object.entries(fields)) {
    if (typeof field.value === 'string' && field.value.length > 0) {
      result[key] = { ...field, value: normalizeDisplayText(field.value, key) };
    } else {
      result[key] = field;
    }
  }
  
  return result;
}
